import React, { memo, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AliceCarousel from "react-alice-carousel";
import "react-alice-carousel/lib/scss/alice-carousel.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { Product, Size } from "../../../gobalType";
import { PATH_IMAGE_URL } from "../../../services/api.constant";
import { TypesAction } from "../../../redux/typesAction";
import { EXPLORER_CARD_SHADOW } from "../explorerTheme";
import {
  pickPromotionImagePath,
} from "../utils/explorerPromotionProduct";
import "./ExplorerPromotionSlider.scss";

type ExplorerPromotionSliderProps = {
  products: Product[];
  catalogSizes: Size[];
};

const ExplorerPromotionSlider: React.FC<ExplorerPromotionSliderProps> = ({
  products,
  catalogSizes,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const preparePlantsList = useCallback(() => {
    dispatch({ type: TypesAction.CATEGORY_ID, payload: 1 });
    dispatch({ type: TypesAction.SHOW_INPUT_SEARCH, payload: true });
  }, [dispatch]);

  const goPlantsByProduct = useCallback(() => {
    preparePlantsList();
    navigate(
      { pathname: "/products-plants", search: "" },
      { state: { explorerSpecialProducts: products } }
    );
  }, [navigate, preparePlantsList, products]);

  if (!products?.length) {
    return (
      <Typography sx={{ textAlign: "center", color: "#666", py: 2 }}>
        No promotion items
      </Typography>
    );
  }

  const slides = products.map((item, index) => {
    const path = pickPromotionImagePath(item);
    const src = path ? PATH_IMAGE_URL + path : "";
    const name = item?.name ?? "";

    return (
      <Box
        key={item.id ?? index}
        sx={{
          boxSizing: "border-box",
          px: 0.5,
        }}
      >
        <Box
          onClick={() => goPlantsByProduct()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              goPlantsByProduct();
            }
          }}
          aria-label={`Open products for ${name}`}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: EXPLORER_CARD_SHADOW,
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            "&:active": { opacity: 0.96 },
            transition: "opacity 0.12s ease",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: "4 / 3",
              minHeight: { xs: 200, sm: 220 },
              maxHeight: 320,
              bgcolor: "#e8e8e8",
            }}
          >
            <Box
              component="img"
              src={src}
              alt={name || "Promotion"}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  });

  return (
    <Box className="explorer-promo-slider explorer-promo-slider--hero" sx={{ width: "100%" }}>
      <AliceCarousel
        mouseTracking
        touchTracking
        disableButtonsControls
        disableDotsControls={false}
        infinite={false}
        paddingLeft={8}
        paddingRight={8}
        responsive={{
          0: {
            items: 1,
            itemsFit: "contain",
          },
        }}
      >
        {slides}
      </AliceCarousel>
    </Box>
  );
};

export default memo(ExplorerPromotionSlider);
